import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import Chart from 'common/chartjs/register';
import EmptyChart from 'legacy/javascripts/components/empty_chart.es6.jsx';

function getChartElements(e, chart) {
  return chart.getElementsAtEventForMode(e, 'nearest', { intersect: true }, false);
}

function defaultLabelHandler(e, chart) {
  let chartElement = getChartElements(e, chart)[0];
  return chart.data.labels[chartElement.index];
}

function normalizeScale(scale = {}) {
  const { gridLines, scaleLabel, ...rest } = scale;

  return {
    ...rest,
    ...(gridLines ? { grid: gridLines } : {}),
    ...(scaleLabel ?
      {
        title: {
          display: scaleLabel.display,
          text: scaleLabel.labelString,
        },
      }
    : {}),
  };
}

function toLegacyTooltipItem(item) {
  return {
    xLabel: item.label,
    yLabel: item.formattedValue,
    index: item.dataIndex,
    datasetIndex: item.datasetIndex,
    _index: item.dataIndex,
    _datasetIndex: item.datasetIndex,
  };
}

function normalizeTooltipCallbacks(callbacks = {}) {
  const normalizedCallbacks = {};

  if (callbacks.title) {
    normalizedCallbacks.title = tooltipItems =>
      callbacks.title(tooltipItems.map(toLegacyTooltipItem), tooltipItems[0]?.chart?.data);
  }

  if (callbacks.label) {
    normalizedCallbacks.label = tooltipItem =>
      callbacks.label(toLegacyTooltipItem(tooltipItem), tooltipItem.chart.data);
  }

  if (callbacks.afterLabel) {
    normalizedCallbacks.afterLabel = tooltipItem =>
      callbacks.afterLabel(toLegacyTooltipItem(tooltipItem), tooltipItem.chart.data);
  }

  if (callbacks.labelColor) {
    normalizedCallbacks.labelColor = tooltipItem =>
      callbacks.labelColor(toLegacyTooltipItem(tooltipItem), tooltipItem.chart);
  }

  if (callbacks.footer) {
    normalizedCallbacks.footer = tooltipItems =>
      callbacks.footer(tooltipItems.map(toLegacyTooltipItem), tooltipItems[0]?.chart?.data);
  }

  return normalizedCallbacks;
}

function normalizeTooltips(tooltips = {}) {
  const { callbacks, xPadding, yPadding, ...rest } = tooltips;

  return {
    ...rest,
    ...(callbacks ? { callbacks: normalizeTooltipCallbacks(callbacks) } : {}),
    ...(xPadding || yPadding ?
      {
        padding: {
          x: xPadding || 0,
          y: yPadding || 0,
        },
      }
    : {}),
  };
}

function normalizeOptions(options = {}, type) {
  const { legend, tooltips, hover, scales, plugins, ...rest } = options;

  const normalizedOptions = {
    ...rest,
    ...(type === 'horizontalBar' ? { indexAxis: 'y' } : {}),
  };

  if (legend || tooltips || plugins) {
    normalizedOptions.plugins = {
      ...(plugins || {}),
      ...(legend ? { legend } : {}),
      ...(tooltips ? { tooltip: normalizeTooltips(tooltips) } : {}),
    };
  }

  if (hover?.animationDuration !== undefined) {
    normalizedOptions.transitions = {
      active: {
        animation: {
          duration: hover.animationDuration,
        },
      },
    };
  }

  if (scales) {
    normalizedOptions.scales = {
      ...(scales.x ? { x: scales.x } : {}),
      ...(scales.y ? { y: scales.y } : {}),
      ...(scales.xAxes ? { x: normalizeScale(scales.xAxes[0]) } : {}),
      ...(scales.yAxes ? { y: normalizeScale(scales.yAxes[0]) } : {}),
    };
  }

  return normalizedOptions;
}

class BaseChart extends Component {
  constructor() {
    super();
    this._chart = null;
    this.canvasRef = createRef();
    this.handleClick = this.handleClick.bind(this);
  }

  handleClick(e) {
    if (!this.props.chartHandler) {
      return;
    }

    const labelHandler = this.props.labelHandler || defaultLabelHandler;

    if (getChartElements(e, this._chart).length > 0) {
      const label = labelHandler(e, this._chart);
      const filters = {
        [this.props.labelParam]: label,
        ...this.props.extraParams,
      };
      this.props.chartHandler({ filters, mixpanelId: this.props.id });
    }
  }

  draw() {
    if (this.props.labels.length > 0) {
      const canvas = this.canvasRef.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');

      const data = {
        labels: this.props.labels,
        datasets: this.props.datasets,
      };

      const responsiveChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
      };

      const type = this.props.type === 'horizontalBar' ? 'bar' : this.props.type;

      this._chart = new Chart(ctx, {
        type,
        data: data,
        options: {
          onClick: this.handleClick,
          ...responsiveChartOptions,
          ...normalizeOptions(this.props.options, this.props.type),
          locale: (window.Localize && window.Localize.getLanguage()) || 'en',
        },
      });
    }
  }

  componentDidMount() {
    this.draw();
  }

  componentDidUpdate(prevProps) {
    if (this.props.labels != prevProps.labels) {
      if (this._chart) {
        this._chart.destroy();
      }
      this.draw();
    }
  }

  render() {
    if (this.props.labels.length > 0) {
      return (
        <div className="canvas-container">
          <canvas id={this.props.id} ref={this.canvasRef} />
        </div>
      );
    } else {
      return <EmptyChart />;
    }
  }
}

export default BaseChart;
